import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { useUpdateUserData, useUserData } from '../queries';
import { Backdrop } from '@mui/material';
import { LanguageType } from '../@types';
import { localizeDayjs } from './dayjs';
import { SELECTED_LANGUAGE_STORAGE_KEY } from './utils';
import { useMarketplaceConfig } from '../queries/marketplace';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import useLocalStorage from '../hooks/useLocalStorage';
import { runtimeEnv } from '../config/runtimeEnv';
import { getLocalizationLoadPath } from './loadPath';

interface LanguageContextType {
  setSelectedLanguage: (lng: LanguageType) => Promise<void>;
}

const sourcePath = getLocalizationLoadPath(runtimeEnv.version);
const defaultLng: LanguageType = 'en';

const setup = (lng: LanguageType) => {
  return i18next
    .use(Backend)
    .use(initReactI18next)
    .init({
      fallbackLng: defaultLng,
      load: 'currentOnly',
      ns: ['common', 'outcomes', 'eventParts', 'markets', 'ftb_markets', 'errors'],
      defaultNS: 'common',
      lng,
      debug: false,
      backend: {
        loadPath: sourcePath,
      },
      returnNull: true,
      interpolation: {
        escapeValue: false,
      },
    });
};

interface Props {
  children: React.ReactElement;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageInstanceProvider = ({ children }: Props): React.ReactElement => {
  const { data: marketplaceData } = useMarketplaceConfig();
  const { data: userData, isLoading: isUserDataLoading } = useUserData();
  const { mutate: updateUserSettings } = useUpdateUserData();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const [isInitialized, setIsInitialized] = useState(false);
  const [localStorageLanguage, setLocalStorageLanguage] = useLocalStorage<LanguageType | null>(
    SELECTED_LANGUAGE_STORAGE_KEY,
    null
  );

  const marketplaceDefaultLanguage = marketplaceData?.marketplaceConfig?.languageConfig?.defaultLanguage ?? defaultLng;

  const targetLanguage = useMemo(() => {
    if (isAuthenticated) {
      if (isUserDataLoading) {
        return localStorageLanguage ?? marketplaceDefaultLanguage;
      }
      return userData?.language ?? marketplaceDefaultLanguage;
    }

    return localStorageLanguage ?? marketplaceDefaultLanguage;
  }, [isAuthenticated, isUserDataLoading, localStorageLanguage, userData?.language, marketplaceDefaultLanguage]);

  useEffect(() => {
    const isWaitingForUserData = isAuthenticated && isUserDataLoading;

    if (!isInitialized && !isWaitingForUserData) {
      setup(targetLanguage).then(() => {
        localizeDayjs(targetLanguage);
        setIsInitialized(true);
      });
    }
  }, [isInitialized, isAuthenticated, isUserDataLoading, targetLanguage]);

  const updateLanguage = useCallback(
    async (lng: LanguageType) => {
      if (!isInitialized) return;

      const currentLanguage = i18next.language;
      if (currentLanguage === lng) return;

      await i18next.changeLanguage(lng);
      localizeDayjs(lng);
      setLocalStorageLanguage(lng);

      if (isAuthenticated) {
        updateUserSettings({ language: lng });
      }

      queryClient.invalidateQueries();
    },
    [queryClient, setLocalStorageLanguage, isInitialized, isAuthenticated, updateUserSettings]
  );

  useEffect(() => {
    if (isInitialized) {
      updateLanguage(targetLanguage);
    }
  }, [targetLanguage, isInitialized, updateLanguage]);

  const contextValue = useMemo(
    () => ({
      setSelectedLanguage: updateLanguage,
    }),
    [updateLanguage]
  );

  if (!isInitialized) {
    return <Backdrop open />;
  }

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
